import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from '@/context/ThemeContext';
import { CartProvider } from '@/context/CartContext';
import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import CustomerDashboard from '@/pages/CustomerDashboard';
import AdminDashboard from '@/pages/AdminDashboard';
import AdminSettings from '@/pages/AdminSettings';
import AdminOffers from '@/pages/AdminOffers';
import AdminOrders from '@/pages/AdminOrders';
import AdminProducts from '@/pages/AdminProducts';
import AdminCategories from '@/pages/AdminCategories';
import QuickScanner from '@/pages/QuickScanner';
import PointsHistoryPage from '@/pages/PointsHistoryPage';
import ProfilePage from '@/pages/ProfilePage';
import OffersPage from '@/pages/OffersPage';
import CardEditor from '@/pages/CardEditor';
import SiteEditor from '@/pages/SiteEditor';
import PhysicalCards from '@/pages/PhysicalCards';
import OrderPage from '@/pages/OrderPage';
import ChangePasswordModal from '@/components/ChangePasswordModal';
import InstallAppBanner from '@/components/InstallAppBanner';
import MaintenancePage from '@/pages/MaintenancePage';
import axios from 'axios';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const hasPermission = (user, ...permissions) => {
  if (user?.role === 'admin') return true;
  return user?.role === 'employee' && permissions.some(permission => (user.permissions || []).includes(permission));
};

const StaffRoute = ({ user, permissions, children }) => {
  if (!user) return <Navigate to="/login" replace />;
  if (!hasPermission(user, ...permissions)) return <Navigate to="/admin" replace />;
  return children;
};


function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/settings`)
      .then(response => setSiteSettings(response.data))
      .catch(() => setSiteSettings(null));

    const checkUserSession = async () => {
      const token = localStorage.getItem('token');
      const cachedUserData = localStorage.getItem('user');

      // Il token resta in localStorage anche dopo la chiusura del browser o
      // dell'app installata. Alla riapertura viene convalidato dal backend.
      if (!token) {
        localStorage.removeItem('user');
        sessionStorage.removeItem('activeAppSession');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const freshUser = response.data;
        localStorage.setItem('user', JSON.stringify(freshUser));
        sessionStorage.removeItem('activeAppSession');
        setUser(freshUser);

        if (freshUser.must_change_password) {
          setShowChangePasswordModal(true);
        }
      } catch (error) {
        const responseStatus = error.response?.status;
        const sessionIsInvalid = [401, 403, 404].includes(responseStatus);

        if (sessionIsInvalid) {
          // Il backend ha confermato che il token è scaduto/non valido oppure
          // che l'account non esiste più. Solo in questi casi facciamo logout.
          console.log('Session invalid, logging out:', responseStatus);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          sessionStorage.removeItem('activeAppSession');
          setUser(null);
        } else {
          // Se Render o la rete non rispondono temporaneamente, non perdiamo
          // l'accesso salvato. Verrà riconvalidato alla prossima apertura.
          console.warn('Session check temporarily unavailable:', error.message);
          try {
            const cachedUser = cachedUserData ? JSON.parse(cachedUserData) : null;
            setUser(cachedUser);
            if (cachedUser?.must_change_password) {
              setShowChangePasswordModal(true);
            }
          } catch {
            setUser(null);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    checkUserSession();
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    sessionStorage.removeItem('activeAppSession');
    setUser(userData);
    // Show modal if must change password
    if (userData.must_change_password) {
      setShowChangePasswordModal(true);
    }
  };

  const handlePasswordChanged = () => {
    // Update user state to remove must_change_password flag
    const updatedUser = { ...user, must_change_password: false };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
    setShowChangePasswordModal(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('activeAppSession');
    setUser(null);
    setShowChangePasswordModal(false);
  };

  if (loading) {
    return <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
      <div className="text-[#5D1822] text-xl">Caricamento...</div>
    </div>;
  }

  const isStaff = user && ['admin', 'employee'].includes(user.role);
  const isAdminAccessPath = window.location.pathname === '/login' || window.location.pathname.startsWith('/admin');
  if (siteSettings?.maintenance_enabled && !isStaff && !isAdminAccessPath) {
    return <MaintenancePage settings={siteSettings} />;
  }

  return (
    <div className="App">
      {/* Banner per installare l'app su mobile */}
      <InstallAppBanner primaryColor="#9B1B30" />
      
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={user ? <LandingPage user={user} /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/offers"
            element={user ? <OffersPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/ordina"
            element={user ? <OrderPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/carrello"
            element={user ? <OrderPage checkoutOnly /> : <Navigate to="/login" replace />}
          />
          <Route 
            path="/login" 
            element={user ? <Navigate to={['admin','employee'].includes(user.role) ? '/admin' : '/dashboard'} /> : <LoginPage onLogin={handleLogin} />} 
          />
          <Route 
            path="/dashboard" 
            element={user && user.role === 'customer' ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/admin" 
            element={user && ['admin','employee'].includes(user.role) ? <AdminDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/admin/settings" 
            element={user && user.role === 'admin' ? <AdminSettings user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/admin/scanner-cassa" 
            element={<StaffRoute user={user} permissions={['scanner.use']}><QuickScanner user={user} onLogout={handleLogout} /></StaffRoute>} 
          />
          <Route 
            path="/admin/offers" 
            element={user && user.role === 'admin' ? <AdminOffers user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/admin/orders" 
            element={<StaffRoute user={user} permissions={['orders.view','orders.view_delivery']}><AdminOrders user={user} onLogout={handleLogout} /></StaffRoute>} 
          />
          <Route
            path="/admin/products"
            element={user && user.role === 'admin' ? <AdminProducts /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/categories"
            element={user && user.role === 'admin' ? <AdminCategories /> : <Navigate to="/login" />}
          />
          <Route 
            path="/admin/points-history" 
            element={user && user.role === 'admin' ? <PointsHistoryPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/admin/card-editor" 
            element={user && user.role === 'admin' ? <CardEditor user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/admin/site-editor" 
            element={user && user.role === 'admin' ? <SiteEditor user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/admin/physical-cards" 
            element={user && user.role === 'admin' ? <PhysicalCards user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/profile" 
            element={user ? <ProfilePage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route
            path="*"
            element={<Navigate to={user ? "/" : "/login"} replace />}
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
      
      {/* Change Password Modal - shown when must_change_password is true */}
      {showChangePasswordModal && (
        <ChangePasswordModal 
          onPasswordChanged={handlePasswordChanged}
          onLogout={handleLogout}
        />
      )}
    </div>
  );
}

function AppWithProviders() {
  return (
    <ThemeProvider>
      <CartProvider>
        <App />
      </CartProvider>
    </ThemeProvider>
  );
}

export default AppWithProviders;